/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { DeleteOutlined } from '@ant-design/icons';
import { VerticalSpace } from '@brokerbay/design-components';
import Axios from 'axios';
import {
  Button,
  Form,
  Input,
  InputNew,
  Page,
  Text,
  Link,
  Select,
  Space,
} from 'components';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

enum LOGIN_DESTINATION {
  HOME = 'Home',
  BOOK_APPOINTMENT = 'Book Appointment',
  MANAGE_BROKERBAY = 'Manage BrokerBay',
}

class ListingInfo {
  listingId = '';
  listingOid = '';
  listingMlsNumber = '';
  idModifiedAt?: Date;
  idFetchedAt?: Date;
}

const utcDate = () => new Date().toISOString().split('T')[0];

const hash = async (string: string) => {
  const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map(bytes => bytes.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
};

const getAuthHost = () => {
  const { host } = window.location;
  const subdomain = host.split('.')[0];
  const processedHost = subdomain === 'edge' ? 'auth' : `auth-${subdomain}`;
  return `https://${processedHost}.brokerbay.com`;
};

const getHomeLink = async ({
  client,
  hashKey,
  mls,
  userId,
  oid,
}: {
  hashKey: string;
  userId: string;
  oid: string;
  mls: string;
  client: string;
}) => {
  const host = getAuthHost();

  const queryString = new URLSearchParams({
    userId,
    oid,
    mls,
  }).toString();
  const hashValue = await hash(hashKey + utcDate() + queryString);
  return `${host}/login/${client}?${queryString}&hash=${hashValue}`;
};

const getBookAppointmentLink = async ({
  client,
  hashKey,
  mls,
  userId,
  userOid,
  listingInfoArray,
  userUniqueId,
}: {
  client: string;
  hashKey: string;
  mls: string;
  userId: string;
  userOid: string;
  listingInfoArray: ListingInfo[];
  userUniqueId: string;
}) => {
  const host = getAuthHost();
  const validListingInfoArray = listingInfoArray.filter(
    info => info.listingOid && info.listingMlsNumber,
  );

  const query = new URLSearchParams({
    mls,
    userId,
    userOid,
    ...(userUniqueId ? { userUniqueId } : {}),
  });

  validListingInfoArray.forEach(info =>
    query.append(
      'listingMlsNumber',
      `${info.listingOid},${info.listingMlsNumber}`,
    ),
  );

  const queryString = query.toString();

  const hashValue = await hash(hashKey + utcDate() + queryString);

  return `${host}/external/book/${client}?${queryString}&hash=${hashValue}`;
};

const getManageBrokerBayLink = async ({
  client,
  hashKey,
  mls,
  userId,
  userOid,
  listingMlsNumber,
}: {
  client: string;
  hashKey: string;
  mls: string;
  userId: string;
  userOid: string;
  listingMlsNumber: string;
}) => {
  const host = getAuthHost();

  const query = new URLSearchParams({
    mls,
    userId,
    userOid,
    listingId: listingMlsNumber,
  });

  const queryString = query.toString();

  const hashValue = await hash(hashKey + utcDate() + queryString);

  return `${host}/external/listing/${client}?${queryString}&hash=${hashValue}`;
};

const getLink = async ({
  destination,
  client,
  hashKey,
  mls,
  userId,
  oid,
  listingInfoArray,
  userUniqueId,
  listingMlsNumber,
}: {
  destination: LOGIN_DESTINATION;
  hashKey: string;
  userId: string;
  oid: string;
  listingInfoArray: ListingInfo[];
  mls: string;
  client: string;
  userUniqueId: string;
  listingMlsNumber: string;
}) => {
  if (destination === LOGIN_DESTINATION.BOOK_APPOINTMENT) {
    return getBookAppointmentLink({
      hashKey,
      userId,
      userOid: oid,
      listingInfoArray,
      mls,
      client,
      userUniqueId,
    });
  }
  if (destination === LOGIN_DESTINATION.MANAGE_BROKERBAY) {
    return getManageBrokerBayLink({
      hashKey,
      userId,
      userOid: oid,
      mls,
      client,
      listingMlsNumber,
    });
  }

  return getHomeLink({
    hashKey,
    userId,
    oid,
    mls,
    client,
  });
};

const fetchListingDoc = async (listingId: string) => {
  try {
    const res = await Axios.get(`/listing/${listingId}`);
    return res.data;
  } catch (e) {
    console.error(e);
    return null;
  }
};

const DirectLoginPage = () => {
  const [destination, setDestination] = useState(LOGIN_DESTINATION.HOME);
  const [result, setResult] = useState({
    errors: {} as Record<string, React.ReactNode>,
    link: '',
  });

  // Home
  const [client, setClient] = useState('');
  const [hashKey, setHashKey] = useState('');
  const [userMlsNumber, setUserMlsNumber] = useState('');
  const [oid, setOid] = useState('');
  const [mlsOuid, setMlsOuid] = useState('');

  // Book Appointment
  const [listingInfoArray, setListingInfoArray] = useState<ListingInfo[]>([
    new ListingInfo(),
  ]);
  const [userUniqueId, setUserUniqueId] = useState('');

  // Manage BrokerBay
  const [listingMlsNumber, setListingMlsNumber] = useState('');

  const setListingInfo = (
    index: number,
    key: keyof Omit<ListingInfo, 'idModifiedAt' | 'idFetchedAt'>,
    value: string,
  ) => {
    setListingInfoArray(prev => {
      const next = [...prev];
      next[index][key] = value;

      if (key === 'listingId') {
        next[index].listingOid = '';
        next[index].listingMlsNumber = '';
        next[index].idModifiedAt = new Date();
      }

      return next;
    });
  };

  useEffect(() => {
    const findAndSetListingInfo = async (array: ListingInfo[]) => {
      let isModified = false;
      const currentTime = new Date();
      const newInfoArray = [...array];

      const listingDocs = await Promise.all(
        newInfoArray.map(x => {
          if (x.listingId.length < 24) return null;

          const idModifiedAt = x.idModifiedAt ?? new Date(0);
          const idFetchedAt = x.idFetchedAt ?? new Date(0);
          if (idModifiedAt <= idFetchedAt) return null;

          return fetchListingDoc(x.listingId);
        }),
      );

      for (let i = 0; i < newInfoArray.length; i++) {
        const info = newInfoArray[i];
        const doc = listingDocs[i];

        if (doc) {
          info.listingOid = doc.oid;
          info.listingMlsNumber = doc.mls_numbers[0].number;
          info.idFetchedAt = currentTime;
          isModified = true;
        }
      }

      if (isModified) setListingInfoArray(newInfoArray);
    };

    findAndSetListingInfo(listingInfoArray);
  }, [listingInfoArray]);

  const getErrorText = (message: string) => <ErrorText>{message}</ErrorText>;

  const requiredValidator = (errorMap: typeof result.errors) => {
    if (!client) errorMap.client = getErrorText('Client cannot be empty');
    if (!hashKey) errorMap.hashKey = getErrorText('Hash key cannot be empty');
    if (!userMlsNumber)
      errorMap.userMlsNumber = getErrorText('User mls number cannot be empty');
    if (!mlsOuid) errorMap.mlsOuid = getErrorText('Mls ouid cannot be empty');

    if (destination === LOGIN_DESTINATION.BOOK_APPOINTMENT) {
      listingInfoArray.forEach((info, i) => {
        if (!info.listingOid || !info.listingMlsNumber)
          errorMap[`listingInfo-${i}`] = getErrorText(
            'Both listing oid and mls number cannot be empty',
          );
      });
    }
  };

  const duplicatedListingValidator = (errorMap: typeof result.errors) => {
    const infoSet = new Set();

    for (let i = 0; i < listingInfoArray.length; i++) {
      const info = listingInfoArray[i];
      const key = `${info.listingOid}:${info.listingMlsNumber}`;
      if (infoSet.has(key)) {
        errorMap[`listingInfo-${i}`] = getErrorText(
          'Duplicated listings are not allowed in one tour',
        );
      }
      infoSet.add(key);
    }

    return errorMap;
  };

  const validate = () => {
    const errorMap = {};
    requiredValidator(errorMap);
    duplicatedListingValidator(errorMap);
    return errorMap;
  };

  return (
    <Page type="filled" title="Test Direct Login QA Page">
      <Layout>
        <Text>This QA page is used to help testing direct login urls</Text>
        <Form labelCol={{ span: 3 }} wrapperCol={{ span: 8 }}>
          <FormItem required label="Login Destination">
            <Select
              value={destination}
              onChange={value => setDestination(value)}
              options={Object.values(LOGIN_DESTINATION).map(dst => ({
                label: dst,
                value: dst,
              }))}
            />
          </FormItem>
          <FormItem
            required
            label="Client info"
            extra={'Auth-server client + decrypted hash key'}
            help={[result.errors.client, result.errors.hashKey]}
          >
            <InputNew.Group compact>
              <HalfWidthInput
                placeholder="Name"
                value={client}
                onChange={({ target: { value } }) => setClient(value)}
              />
              <HalfWidthInput
                placeholder="Key"
                type="password"
                value={hashKey}
                onChange={({ target: { value } }) => setHashKey(value)}
              />
            </InputNew.Group>
          </FormItem>
          <FormItem
            required
            label="User info"
            extra={`User's office id + MLS number (office id does not need to be an exact value 99% of the time as it can be anything, even empty)`}
            help={result.errors.userMlsNumber}
          >
            <InputNew.Group compact>
              <HalfWidthInput
                placeholder="User oid"
                value={oid}
                onChange={({ target: { value } }) => setOid(value)}
              />

              <HalfWidthInput
                placeholder="User MLS number"
                value={userMlsNumber}
                onChange={({ target: { value } }) => setUserMlsNumber(value)}
              />
            </InputNew.Group>
          </FormItem>

          {destination === LOGIN_DESTINATION.MANAGE_BROKERBAY && (
            <FormItem
              required
              label="Listing info"
              extra={`Listing's MLS number`}
              help={result.errors.userMlsNumber}
            >
              <InputNew.Group compact>
                <FullWidthInput
                  placeholder="Listing MLS number"
                  value={listingMlsNumber}
                  onChange={({ target: { value } }) =>
                    setListingMlsNumber(value)
                  }
                />
              </InputNew.Group>
            </FormItem>
          )}

          <FormItem
            required
            label="MLS ouid"
            extra={'Ouid of the MLS which the user belongs to'}
            help={result.errors.mlsOuid}
          >
            <Input
              placeholder="MLS ouid"
              value={mlsOuid}
              onChange={({ target: { value } }) => setMlsOuid(value)}
            />
          </FormItem>

          {destination === LOGIN_DESTINATION.BOOK_APPOINTMENT && (
            <>
              {listingInfoArray.map((info, index) => (
                <FormItem
                  required
                  key={`listingInfo-${index}`}
                  label={
                    <>
                      <Button
                        danger
                        type="primary"
                        icon={<DeleteOutlined style={{ color: 'white' }} />}
                        style={{ marginRight: 8 }}
                        onClick={() => {
                          setListingInfoArray(prev => [
                            ...prev.slice(0, index),
                            ...prev.slice(index + 1, prev.length),
                          ]);
                        }}
                      />
                      Listing info {index + 1}
                    </>
                  }
                  extra={`Listing's oid + MLS number (Disable input when entering Listing id)`}
                  help={result.errors[`listingInfo-${index}`]}
                >
                  <InputNew.Group compact>
                    <FullWidthInput
                      style={{ marginBottom: 8 }}
                      placeholder="Listing Id (Automatically get the oid and mls number)"
                      value={info.listingId}
                      onChange={({ target: { value } }) =>
                        setListingInfo(index, 'listingId', value)
                      }
                    />
                    <InputNew.Group compact>
                      <HalfWidthInput
                        placeholder="Listing oid"
                        value={info.listingOid}
                        disabled={Boolean(info.listingId)}
                        onChange={({ target: { value } }) =>
                          setListingInfo(index, 'listingOid', value)
                        }
                      />
                      <HalfWidthInput
                        placeholder="Listing MLS number"
                        value={info.listingMlsNumber}
                        disabled={Boolean(info.listingId)}
                        onChange={({ target: { value } }) =>
                          setListingInfo(index, 'listingMlsNumber', value)
                        }
                      />
                    </InputNew.Group>
                  </InputNew.Group>
                </FormItem>
              ))}
              <FormItem label={' '} colon={false}>
                <Button
                  block
                  onClick={async () => {
                    setListingInfoArray(prev => [...prev, new ListingInfo()]);
                  }}
                >
                  Add Listing
                </Button>
              </FormItem>
              <FormItem label="User Unique Id" extra={'Optional'}>
                <Input
                  placeholder="MlsUniqueId"
                  value={userUniqueId}
                  onChange={({ target: { value } }) => setUserUniqueId(value)}
                />
              </FormItem>
            </>
          )}

          <FormItem wrapperCol={{ span: 11 }}>
            <SubmitButton
              block
              type="primary"
              onClick={async () => {
                const errorMap = validate();
                if (Object.keys(errorMap).length) {
                  setResult({ errors: errorMap, link: '' });
                  return;
                }

                const generatedLink = await getLink({
                  destination,
                  client,
                  hashKey,
                  userId: userMlsNumber,
                  oid,
                  mls: mlsOuid,
                  listingInfoArray,
                  userUniqueId,
                  listingMlsNumber,
                });

                setResult({ errors: {}, link: generatedLink });
              }}
            >
              Generate
            </SubmitButton>
          </FormItem>
        </Form>

        {result.link && (
          <VerticalSpace>
            <Text block>{result.link}</Text>
            <Space>
              <Button
                onClick={() => navigator.clipboard.writeText(result.link)}
              >
                Copy Url
              </Button>
              <Link href={result.link} target="_blank">
                <Button>Go to link</Button>
              </Link>
            </Space>
          </VerticalSpace>
        )}
      </Layout>
    </Page>
  );
};

export default DirectLoginPage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 32px;
  padding: 20px;
  background: white;
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 0px;
`;

const FullWidthInput = styled(Input)`
  width: 100%;
`;

const HalfWidthInput = styled(Input)`
  width: 50%;
`;

const ErrorText = styled.span`
  color: #ff4d4f;
  display: block;
`;

const SubmitButton = styled(Button)`
  margin-top: 32px;
`;
